import React from "react";
import { ShieldCheck, Languages, Award, Users } from "lucide-react";
import { Reveal, SectionLabel, scrollToId } from "./Reveal";
import { STATS } from "../../data";
import { useLang } from "../../i18n";

const CRED_ICONS = [Award, Languages, ShieldCheck, Users];

export const Instructor = () => {
  const { t } = useLang();
  const ins = t.instructor;
  return (
    <section
      id="instructor"
      data-testid="instructor-section"
      className="relative py-24 sm:py-32 bg-[#0a1430] border-y border-white/10 overflow-hidden"
    >
      <div className="absolute -left-20 top-10 h-72 w-72 rounded-full bg-blue-600/15 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden card-hairline aspect-[4/5] flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400/10 to-transparent" />
              <div className="text-center px-8">
                <div className="mx-auto h-32 w-32 rounded-full border-2 border-amber-400/40 bg-amber-400/10 flex items-center justify-center font-head font-extrabold text-3xl text-gold-grad">
                  GSSC
                </div>
                <div className="mt-6 font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white leading-tight">{ins.name}</div>
                <div className="mt-1 font-mono-x text-xs uppercase tracking-[0.25em] text-amber-400">{ins.role}</div>
                <div className="mt-6 flex justify-center gap-2 flex-wrap">
                  {ins.tags.map((tag) => (
                    <span key={tag} className="text-[11px] font-mono-x uppercase tracking-wider text-slate-300 border border-white/10 rounded-full px-3 py-1">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel num="[ 06 ]">{ins.label}</SectionLabel>
              <h2 className="font-head font-extrabold uppercase tracking-tight text-white text-4xl sm:text-5xl leading-[0.9]">
                {ins.title1}
                <span className="block text-gold-grad">{ins.title2}</span>
              </h2>
              <p className="mt-6 text-slate-400 leading-relaxed max-w-xl">{ins.body}</p>
            </Reveal>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {ins.creds.map((cred, i) => {
                const Icon = CRED_ICONS[i];
                return (
                  <Reveal key={cred} delay={i * 0.05}>
                    <div className="flex items-center gap-3 rounded-xl card-hairline px-5 py-4">
                      <Icon size={18} className="text-amber-400 shrink-0" />
                      <span className="text-sm text-slate-200">{cred}</span>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={0.1}>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {STATS.map((s, i) => (
                  <div key={i}>
                    <div className="font-head font-extrabold text-3xl sm:text-4xl text-white">
                      {s.value}
                      <span className="text-amber-400">{s.suffix}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{ins.stats[i]}</div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <button
                onClick={() => scrollToId("quote")}
                data-testid="instructor-cta"
                className="mt-9 inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-[#060c1f] font-head font-bold uppercase tracking-wide px-7 py-3.5 rounded-md transition-colors"
              >
                {ins.cta}
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
